import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CheckoutSecureNote } from "@/components/PaymentTrust";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const UpgradeDialog = ({ open, onOpenChange, currentPlan, onUpgraded, intendedPlan, intendedBillingPeriod }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [keyId, setKeyId] = useState("");
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/billing/plans").then((r) => {
      const list = r.data.plans || [];
      setPlans(list);
      setKeyId(r.data.razorpay_key_id || "");
      // Pre-select the plan the visitor chose at signup (intended_plan) when it's valid and not the
      // current plan; otherwise fall back to the first non-current plan. NEVER auto-launch Razorpay.
      const intendedValid = list.find((p) => p.key === intendedPlan && p.key !== currentPlan && !p.is_payg);
      const def = intendedValid || list.find((p) => p.key !== currentPlan) || list[0];
      setSelected(def?.key || null);
      // Pre-select billing period from signup intent if that plan supports it.
      const wantsAnnual = intendedBillingPeriod === "annual";
      setPeriod(wantsAnnual && (def?.billing_periods || []).includes("annual") ? "annual" : "monthly");
    }).catch(() => toast.error("Could not load plans"));
  }, [open, currentPlan, intendedPlan, intendedBillingPeriod]);

  const selectedPlan = plans.find((p) => p.key === selected) || {};
  const supportsAnnual = (selectedPlan.billing_periods || []).includes("annual");
  const effectivePeriod = supportsAnnual ? period : "monthly";
  const priceForSelected = effectivePeriod === "annual" ? (selectedPlan.annual_price || 0) : (selectedPlan.monthly_price ?? selectedPlan.price ?? 0);

  const pay = async () => {
    if (!selected) return;
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) { toast.error("Failed to load payment gateway"); setPaying(false); return; }
      const { data } = await api.post("/billing/create-order", { plan: selected, billing_period: effectivePeriod });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id || keyId,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "RecoverOS",
        description: `${selected} plan — ${effectivePeriod} subscription`,
        prefill: data.prefill || {},
        theme: { color: "#0B3D91" },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: selected,
            });
            window.dispatchEvent(new Event("recoveros:usage-changed"));
            onUpgraded?.();
            onOpenChange(false);
            navigate(`/payment-success?plan=${encodeURIComponent(selected)}&payment_id=${encodeURIComponent(resp.razorpay_payment_id)}`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment could not be verified");
          } finally {
            setPaying(false);
          }
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Upgrade your plan</DialogTitle>
          <DialogDescription>Pick a plan and pay securely via Razorpay. Limits are lifted instantly.</DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 border border-border bg-secondary/40 p-0.5 rounded-sm" data-testid="upgrade-period-toggle">
            <button type="button" onClick={() => setPeriod("monthly")} data-testid="upgrade-period-monthly"
              className={`px-3 py-1 text-xs font-semibold rounded-sm transition-colors ${effectivePeriod === "monthly" ? "bg-background text-[#0B3D91] shadow-sm" : "text-muted-foreground"}`}>Monthly</button>
            <button type="button" onClick={() => setPeriod("annual")} disabled={!supportsAnnual} data-testid="upgrade-period-annual"
              className={`px-3 py-1 text-xs font-semibold rounded-sm transition-colors inline-flex items-center gap-1.5 ${effectivePeriod === "annual" ? "bg-background text-[#0B3D91] shadow-sm" : "text-muted-foreground"} ${!supportsAnnual ? "opacity-40 cursor-not-allowed" : ""}`}>
              Annual {supportsAnnual && selectedPlan.annual_savings_label && <span className="text-[10px] font-bold text-[#16A34A]">· {selectedPlan.annual_savings_label}</span>}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {plans.map((p) => {
            const active = selected === p.key;
            const isCurrent = currentPlan === p.key;
            const canAnnual = (p.billing_periods || []).includes("annual");
            const showAnnual = effectivePeriod === "annual" && canAnnual;
            const cardPrice = showAnnual ? (p.annual_price || 0) : (p.monthly_price ?? p.price ?? 0);
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelected(p.key)}
                disabled={isCurrent}
                data-testid={`upgrade-plan-${p.key}`}
                className={`text-left rounded-sm border p-4 transition-colors ${
                  active ? "border-[#0B3D91] ring-1 ring-[#0B3D91] bg-[#0B3D91]/5"
                  : "border-border hover:border-[#0B3D91]/40"} ${isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold">{p.key}</span>
                  {active && <Check className="h-4 w-4 text-[#16A34A]" />}
                </div>
                <div className="mt-1 text-2xl font-bold">{fmtINR(cardPrice)}<span className="text-xs font-normal text-muted-foreground">/{showAnnual ? "yr" : "mo"}</span></div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {p.account_limit.toLocaleString("en-IN")} accounts · {p.user_limit} users
                </div>
                {isCurrent && <div className="mt-1 text-[11px] font-semibold text-muted-foreground">Current plan</div>}
              </button>
            );
          })}
        </div>

        <Button
          onClick={pay}
          disabled={paying || !selected}
          className="mt-3 w-full rounded-sm bg-[#16A34A] hover:bg-[#138a3e] gap-1.5"
          data-testid="upgrade-pay-btn"
        >
          {paying ? "Processing…" : <>Pay {selected ? fmtINR(priceForSelected) : ""}{effectivePeriod === "annual" ? "/yr" : ""} & Upgrade <ArrowRight className="h-4 w-4" /></>}
        </Button>
        <CheckoutSecureNote className="mt-3" />
      </DialogContent>
    </Dialog>
  );
};
